import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaTrophy, FaMedal, FaStar, FaHeart, FaCamera } from 'react-icons/fa6';
import { FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/Instance';
import { toast } from 'sonner';

const GALLERY_CATEGORIES = [
  "Teaser",
  "Full Body",
  "Chest & Stomach",
  "Butt",
  "P/V",
  "Legs",
  "Feet",
];

const CreatorCard = ({
  id,
  name,
  img,
  age,
  bust,
  penisSize,
  bodyType,
  hairColor,
  skinTone,
  height,
  weight,
  shoeSize,
  photoGallery = {}, // Contains real images from DB
  gender,
  contests = 0, // From DB
  badges = 0, // From DB
  top10 = 0, // From DB
  onClick: onCardClick,
  detailedView = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [favoriteImages, setFavoriteImages] = useState(new Set());
  const [favoriteGalleries, setFavoriteGalleries] = useState(new Set());
  const { profile } = useAuth();

  const extractNumeric = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const hasWalletDeposit = useMemo(() => {
    const possibleAmounts = [
      extractNumeric(profile?.walletBalance),
      extractNumeric(profile?.wallet_balance),
      extractNumeric(profile?.wallet?.balance),
      extractNumeric(profile?.wallet?.available_balance),
      extractNumeric(profile?.wallet?.deposited),
      extractNumeric(profile?.availableBalance),
      extractNumeric(profile?.available_balance),
      extractNumeric(profile?.lastDepositAmount),
      extractNumeric(profile?.last_deposit_amount),
    ].filter((val) => val !== null);

    const maxAmount = possibleAmounts.length ? Math.max(...possibleAmounts) : 0;
    return (
      profile?.hasWalletDeposit === true ||
      profile?.has_wallet_deposit === true ||
      (Number.isFinite(maxAmount) && maxAmount > 0)
    );
  }, [profile]);

  const userRole = profile?.role;

  const hasBackendUnlockedTeaser = useCallback((gallery) => {
    const images = Array.isArray(gallery?.images) ? gallery.images : [];
    const hasBackendLockMetadata = images.some((image) =>
      Object.prototype.hasOwnProperty.call(image || {}, "locked")
    );

    return hasBackendLockMetadata && images.some((image) => (
      image?.locked === false && Boolean(image?.image_url)
    ));
  }, []);

  const canAccessTeaserGallery = useCallback((gallery) => {
    if (gallery?.name !== "Teaser" || userRole !== "user") return true;
    return hasWalletDeposit || hasBackendUnlockedTeaser(gallery);
  }, [hasBackendUnlockedTeaser, hasWalletDeposit, userRole]);

  // Transform photoGallery object into array format
  const galleries = useMemo(() => {
    return GALLERY_CATEGORIES.map((category) => {
      const images = photoGallery[category] || [];
      const hasActiveImage = images.some((img) => img.is_in_contest === true);
      return {
        name: category,
        price: category === "Teaser" ? "Free" : "TBD",
        development: images.length === 0,
        trophies: 0,
        medals: 0,
        ribbons: 0,
        photos: images.length,
        images,
        hasActiveImage,
      };
    });
  }, [photoGallery]);

  const completedGalleries = useMemo(
    () => galleries.filter(gallery => gallery.photos > 0).length,
    [galleries]
  );

  const photoGalleryProgress = `${completedGalleries} of ${GALLERY_CATEGORIES.length}`;

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}accounts/favorites/`, {
        headers: {
          'Authorization': `Bearer ${profile?.accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📥 Fetched favorites:', data);
        const favoriteIds = new Set(data.map(fav => fav.body_part_image_id));
        console.log('❤️ Favorite image IDs:', Array.from(favoriteIds));
        setFavoriteImages(favoriteIds);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  }, [profile?.accessToken]);

  const openGallery = useCallback((gallery) => {
    const isTeaser = gallery?.name === "Teaser";
    const images = Array.isArray(gallery?.images) ? gallery.images : [];
    const visibleImages = images.filter((image) => Boolean(image?.image_url));

    if (isTeaser && !canAccessTeaserGallery(gallery)) {
      toast.info('Deposit funds to your wallet to unlock the Teaser gallery.');
      return;
    }

    if (isTeaser && visibleImages.length === 0) {
      toast.info('No images available in this gallery yet.');
      return;
    }

    setSelectedGallery({
      ...gallery,
      images: visibleImages,
    });

    if (!id || !profile?.accessToken || !gallery?.name) return;

    fetch(`${API_BASE}accounts/profile/record-gallery-view/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${profile.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        contributor_id: id,
        body_part: gallery.name,
      })
    }).then(async (response) => {
      if (response.status === 403 && isTeaser) {
        const data = await response.json().catch(() => ({}));
        toast.error(data?.error || 'Deposit funds to your wallet to unlock the Teaser gallery.');
        setSelectedGallery(null);
      }
    }).catch((error) => {
      console.error('Error recording contributor gallery view:', error);
    });
  }, [canAccessTeaserGallery, id, profile?.accessToken]);

  // Fetch user's favorite images on component mount when modal is open
  useEffect(() => {
    if (isModalOpen && profile?.accessToken) {
      fetchFavorites();
    }
  }, [isModalOpen, profile?.accessToken, fetchFavorites]);

  const toggleFavorite = async (imageId) => {
    console.log('🔄 Toggling favorite for image ID:', imageId);
    console.log('❤️ Current favorites:', Array.from(favoriteImages));
    
    if (!profile?.accessToken) {
      toast.error('Please login to favorite images');
      return;
    }
    
    try {
      const isFavorite = favoriteImages.has(imageId);
      console.log(`Is currently favorite? ${isFavorite}`);
      
      if (isFavorite) {
        // Remove from favorites - DELETE method with ID in URL
        console.log('🗑️ Removing from favorites...');
        const response = await fetch(`${API_BASE}accounts/favorites/remove/${imageId}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${profile.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Response status:', response.status);
        if (response.ok || response.status === 204) {
          setFavoriteImages(prev => {
            const newSet = new Set(prev);
            newSet.delete(imageId);
            console.log('✅ Updated favorites after remove:', Array.from(newSet));
            return newSet;
          });
          toast.success('Removed from favorites');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Failed to remove:', errorData);
          toast.error('Failed to remove from favorites');
        }
      } else {
        // Add to favorites
        console.log('➕ Adding to favorites...');
        const response = await fetch(`${API_BASE}accounts/favorites/add/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${profile.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ body_part_image_id: imageId })
        });
        
        console.log('Response status:', response.status);
        if (response.ok) {
          setFavoriteImages(prev => {
            const newSet = new Set([...prev, imageId]);
            console.log('✅ Updated favorites after add:', Array.from(newSet));
            return newSet;
          });
          toast.success('Added to favorites ❤️');
        } else {
          const errorData = await response.json();
          console.error('❌ Failed to add:', errorData);
          toast.error('Failed to add to favorites');
        }
      }
    } catch (error) {
      console.error('❌ Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const toggleGalleryFavorite = (galleryName) => {
    setFavoriteGalleries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(galleryName)) {
        newSet.delete(galleryName);
        toast.success(`Removed "${galleryName}" from favorites`);
      } else {
        newSet.add(galleryName);
        toast.success(`Added "${galleryName}" to favorites ❤️`);
      }
      return newSet;
    });
  };

  const handleCardClick = () => {
    if (onCardClick) onCardClick();
    else setIsModalOpen(true);
  };

  if (detailedView) {
    return (
      <div className="text-white">
        {/* Inline full-details content (same as modal body); galleries keep wallet gating */}
        <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start mb-6 w-full sm:justify-center">
          <div className="w-full sm:w-auto flex justify-center sm:justify-start">
            {img ? (
              <img src={img} alt={`${name}'s profile`} className="w-28 h-28 object-cover rounded-lg shadow-md border-2 border-[#ceb46a]" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="112" height="112"><rect fill="%23ccc" width="112" height="112"/><text x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em" fill="%23000">No Photo</text></svg>'; }} />
            ) : (
              <div className="w-28 h-28 bg-gradient-to-br from-[#ceb46a] to-[#b99d55] text-white font-bold flex items-center justify-center rounded-lg text-xs text-center shadow-md border-2 border-[#ceb46a]"><span className="text-2xl">👤</span></div>
            )}
          </div>
          <div className="flex-1 text-sm space-y-1 leading-relaxed">
            <div className="text-lg font-bold text-[#ceb46a] mb-2">{name}</div>
            <p className="text-xs bg-[#2a2a2a] text-[#ceb46a] inline-block px-3 py-1 rounded-full border border-[#ceb46a] mb-2"><strong>Contributor ID:</strong> {id}</p>
            <p><strong>Age:</strong> {age}</p>
            <p><strong>Body Type:</strong> {bodyType}</p>
            <p><strong>Skin Tone:</strong> {skinTone}</p>
            <p><strong>Hair Color:</strong> {hairColor}</p>
            <p><strong>Height:</strong> {height}</p>
            <p><strong>Weight:</strong> {weight} lbs</p>
            {gender === 'Female' && bust && <p><strong>Bust:</strong> {bust}</p>}
            {gender === 'Male' && penisSize && <p><strong>Penis Size:</strong> {penisSize}</p>}
            <p><strong>Shoe Size:</strong> {shoeSize}</p>
            <p><strong>Photo Galleries:</strong> {photoGalleryProgress}</p>
            <div className="flex flex-wrap gap-2 mt-3 text-sm">
              <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-3 py-1 rounded-full text-xs font-semibold"><FaTrophy className="text-[#ceb46a]" /> x{contests}</div>
              <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-3 py-1 rounded-full text-xs font-semibold"><FaMedal className="text-[#ceb46a]" /> x{badges}</div>
              <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-3 py-1 rounded-full text-xs font-semibold"><FaStar className="text-[#ceb46a]" /> x{top10}</div>
            </div>
          </div>
        </div>
        <div className="text-lg text-[#ceb46a] font-semibold mb-4 text-center">Photo Galleries</div>
        <div className="flex flex-col gap-4">
          {galleries.map((gallery, index) => {
            const isTeaser = gallery.name === "Teaser";
            const canUserAccessTeaser = canAccessTeaserGallery(gallery);
            const isTeaserUnlocked = isTeaser && gallery.photos > 0 && canUserAccessTeaser;
            const isClickable = isTeaserUnlocked;
            return (
              <div key={index} onClick={() => { if (isTeaser && !canUserAccessTeaser) { toast.info('Deposit funds to your wallet to unlock the Teaser gallery.'); return; } if (isTeaser && gallery.photos === 0) { toast.info('No images available in this gallery yet.'); return; } if (isClickable) openGallery(gallery); }} className={`${isClickable ? 'cursor-pointer hover:opacity-90' : 'opacity-60 cursor-not-allowed'} bg-[#CEB46A] text-black px-3 sm:px-4 pt-3 pb-2 rounded-md shadow-md w-full max-w-full sm:max-w-md mx-auto relative transition-all overflow-hidden`}>
                <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                  <button onClick={(e) => { e.stopPropagation(); toggleGalleryFavorite(gallery.name); }} className="transition-colors p-1 hover:scale-110 transform" title={favoriteGalleries.has(gallery.name) ? "Remove from favorites" : "Add to favorites"}>
                    {favoriteGalleries.has(gallery.name) ? <FaHeart className="text-lg text-red-500" /> : <svg className="w-5 h-5 text-gray-700 hover:text-red-500 transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>}
                  </button>
                  <span className="text-black text-xs sm:text-sm font-semibold whitespace-nowrap">{isTeaser ? "Free" : "TBD"}</span>
                </div>
                <div className="flex justify-center items-center gap-2 mb-2 flex-wrap">
                  <h3 className={`text-base sm:text-lg font-bold text-center break-words ${!isTeaser && 'opacity-50'}`}>{gallery.name}</h3>
                  <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold flex-shrink-0"><FaCamera className="text-black" /><span>x{gallery.photos}</span></div>
                </div>
                {isTeaser ? (canUserAccessTeaser ? (gallery.hasActiveImage && gallery.photos > 0 ? <div className="absolute bottom-2 left-2 bg-green-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">Active</div> : <div className="absolute bottom-2 left-2 bg-gray-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">Inactive</div>) : <div className="absolute bottom-2 left-2 bg-red-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">Deposit Required</div>) : (gallery.hasActiveImage && gallery.photos > 0 ? <div className="absolute bottom-2 left-2 bg-green-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">Active</div> : <div className="absolute bottom-2 left-2 bg-gray-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">Inactive</div>)}
                {isTeaser && !canUserAccessTeaser && <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px] flex items-center justify-center text-center px-3 text-xs font-semibold text-white">Deposit funds to unlock this gallery.</div>}
                <div className="flex flex-wrap justify-end gap-1 sm:gap-2 mt-3">
                  <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-2 sm:px-3 py-[2px] rounded-full font-semibold text-[10px] sm:text-xs"><FaTrophy className="text-[#ceb46a]" /> <span>x{gallery.trophies}</span></div>
                  <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-2 sm:px-3 py-[2px] rounded-full font-semibold text-[10px] sm:text-xs"><FaMedal className="text-[#ceb46a]" /> <span>x{gallery.medals || 0}</span></div>
                  <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-2 sm:px-3 py-[2px] rounded-full font-semibold text-[10px] sm:text-xs"><FaStar className="text-[#ceb46a]" /> <span>x{gallery.ribbons}</span></div>
                </div>
              </div>
            );
          })}
        </div>
        {selectedGallery && (
          <div className="fixed inset-0 z-[999] bg-black bg-opacity-90 flex items-center justify-center px-4">
            <div className="bg-[#1C1C1E] text-white border border-[#ceb46a] rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
              <button onClick={() => setSelectedGallery(null)} className="absolute top-3 right-3 text-[#ceb46a] text-xl z-10" title="Close"><FaTimes /></button>
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-[#ceb46a]">{selectedGallery.name}</h2>
                <p className="text-sm mt-1 text-gray-300">{selectedGallery.price === "Free" ? "Free" : selectedGallery.price} • {selectedGallery.photos} {selectedGallery.photos === 1 ? 'photo' : 'photos'}</p>
              </div>
              <div className="flex justify-center gap-2 mb-6">
                <div className="flex items-center gap-1 bg-[#ceb46a] text-black px-3 py-1 rounded-full font-semibold text-xs shadow-sm"><FaTrophy /> x{selectedGallery.trophies}</div>
                <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-3 py-1 rounded-full font-semibold text-xs"><FaMedal className="text-[#ceb46a]" /> x{selectedGallery.medals || 0}</div>
                <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-3 py-1 rounded-full font-semibold text-xs"><FaStar className="text-[#ceb46a]" /> x{selectedGallery.ribbons}</div>
              </div>
              {selectedGallery.images && selectedGallery.images.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {selectedGallery.images.map((image, idx) => (
                    <div key={image.id || idx} className="relative group cursor-pointer" onClick={() => setSelectedImage(image)}>
                      <img src={image.image_url} alt={`${selectedGallery.name} ${idx + 1}`} className="w-full h-48 object-cover rounded-lg border-2 border-[#ceb46a] hover:border-[#d6c074] transition-all" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23333" width="200" height="200"/><text x="50%" y="50%" font-size="14" text-anchor="middle" dy=".3em" fill="%23ceb46a">Image Error</text></svg>'; }} />
                      <button onClick={(e) => { e.stopPropagation(); toggleFavorite(image.id); }} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full">{favoriteImages.has(image.id) ? <FaHeart className="text-lg text-red-500" /> : <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>}</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center">No images in this gallery.</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
     <div
  onClick={handleCardClick}
  className="cursor-pointer bg-black text-white p-4 rounded-xl border border-[#ceb46a] shadow-md hover:shadow-lg transition-all w-full flex flex-col h-full"
>
 <div className="flex flex-col gap-4 items-start w-full">
 <div className="w-full flex justify-center">
  {img ? (
    <img 
      src={img} 
      alt={`${name}'s profile`}
      className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-lg shadow-md border border-[#ceb46a] flex-shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="112" height="112"><rect fill="%23ccc" width="112" height="112"/><text x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em" fill="%23000">Image Error</text></svg>';
      }}
    />
  ) : (
    <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-[#ceb46a] to-[#b99d55] text-white font-bold flex flex-col items-center justify-center rounded-lg text-xs text-center shadow-md border border-[#ceb46a] flex-shrink-0">
      <span className="text-2xl mb-1">👤</span>
      <span>No Photo</span>
    </div>
  )}
</div>
    <div className="w-full text-xs sm:text-sm space-y-1 overflow-hidden">
      <div className="flex flex-col gap-1 mb-2">
        <div className="text-base sm:text-lg font-bold text-[#ceb46a] truncate" title={name}>{name}</div>
        <span className="text-xs px-2 py-1 bg-[#2a2a2a] text-[#ceb46a] rounded-full border border-[#ceb46a] w-fit">
          ID: {id}
        </span>
      </div>
      <p className="truncate"><strong>Age:</strong> {age || 'N/A'}</p>
      <p className="truncate"><strong>Body Type:</strong> {bodyType || 'N/A'}</p>
      <p className="truncate"><strong>Skin Tone:</strong> {skinTone || 'N/A'}</p>
      <p className="truncate"><strong>Hair Color:</strong> {hairColor || 'N/A'}</p>
      <p className="truncate"><strong>Height:</strong> {height || 'N/A'}</p>
      <p className="truncate"><strong>Weight:</strong> {weight ? `${weight} lbs` : 'N/A'}</p>
      {gender === 'Female' && bust && <p className="truncate"><strong>Bust:</strong> {bust}</p>}
      {gender === 'Male' && penisSize && <p className="truncate"><strong>Penis Size:</strong> {penisSize}</p>}
      <p className="truncate"><strong>Shoe Size:</strong> {shoeSize || 'N/A'}</p>
      <p className="truncate"><strong>Galleries:</strong> {photoGalleryProgress}</p>
    </div>
  </div>

 <div className="flex flex-wrap justify-center gap-2 mt-4 pt-2 border-t border-gray-700">
  <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
    <FaTrophy className="text-[#ceb46a]" /> <span>x{contests}</span>
  </div>
  <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
    <FaMedal className="text-[#ceb46a]" /> <span>x{badges}</span>
  </div>
  <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
    <FaStar className="text-[#ceb46a]" /> <span>x{top10}</span>
  </div>
</div>

</div>


 {isModalOpen && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center px-4">
    <div className="bg-black border border-[#ceb46a] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative text-white">
      
      <button
        onClick={() => setIsModalOpen(false)}
        className="absolute top-3 right-3 text-[#ceb46a] text-xl"
        title="Close"
      >
        <FaTimes />
      </button>

      <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start mb-6 w-full sm:justify-center">
  <div className="w-full sm:w-auto flex justify-center sm:justify-start">
    {img ? (
      <img 
        src={img} 
        alt={`${name}'s profile`}
        className="w-28 h-28 sm:w-28 sm:h-28 object-cover rounded-lg shadow-md border-2 border-[#ceb46a]"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="112" height="112"><rect fill="%23ccc" width="112" height="112"/><text x="50%" y="50%" font-size="12" text-anchor="middle" dy=".3em" fill="%23000">Image Error</text></svg>';
        }}
      />
    ) : (
      <div className="w-28 h-28 sm:w-28 sm:h-28 bg-gradient-to-br from-[#ceb46a] to-[#b99d55] text-white font-bold flex flex-col items-center justify-center rounded-lg text-xs text-center shadow-md border-2 border-[#ceb46a]">
        <span className="text-2xl mb-1">👤</span>
        <span>No Photo</span>
      </div>
    )}
  </div>




        <div className="flex-1 text-sm space-y-1 leading-relaxed">
          <div className="text-lg font-bold text-[#ceb46a] mb-2">{name}</div>
          <p className="text-xs bg-[#2a2a2a] text-[#ceb46a] inline-block px-3 py-1 rounded-full border border-[#ceb46a] mb-2">
            <strong>Contributor ID:</strong> {id}
          </p>
          <p><strong>Age:</strong> {age}</p>
          <p><strong>Body Type:</strong> {bodyType}</p>
          <p><strong>Skin Tone:</strong> {skinTone}</p>
          <p><strong>Hair Color:</strong> {hairColor}</p>
          <p><strong>Height:</strong> {height}</p>
          <p><strong>Weight:</strong> {weight} lbs</p>
          {gender === 'Female' && bust && <p><strong>Bust:</strong> {bust}</p>}
          {gender === 'Male' && penisSize && <p><strong>Penis Size:</strong> {penisSize}</p>}
          <p><strong>Shoe Size:</strong> {shoeSize}</p>
          <p><strong>Photo Galleries:</strong> {photoGalleryProgress}</p>
          <div className="flex flex-wrap gap-2 mt-3 text-sm">
            <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-3 py-1 rounded-full text-xs font-semibold">
              <FaTrophy className="text-[#ceb46a]" /> x{contests}
            </div>
            <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-3 py-1 rounded-full text-xs font-semibold">
              <FaMedal className="text-[#ceb46a]" /> x{badges}
            </div>
            <div className="flex items-center gap-1 border border-[#ceb46a] text-white px-3 py-1 rounded-full text-xs font-semibold">
              <FaStar className="text-[#ceb46a]" /> x{top10}
            </div>
          </div>
        </div>
      </div>
      <div className="text-lg text-[#ceb46a] font-semibold mb-4 text-center">
        Photo Galleries
      </div>
      <div className="flex flex-col gap-4">
        {galleries.map((gallery, index) => {
          // Only "Teaser" category is clickable
          const isTeaser = gallery.name === "Teaser";
          const canUserAccessTeaser = canAccessTeaserGallery(gallery);
          const isTeaserUnlocked = isTeaser && gallery.photos > 0 && canUserAccessTeaser;
          const isClickable = isTeaserUnlocked;
          
          return (
          <div
            key={index}
            onClick={() => {
              if (isTeaser) {
                if (!canUserAccessTeaser) {
                  toast.info('Deposit funds to your wallet to unlock the Teaser gallery.');
                  return;
                }
                if (gallery.photos === 0) {
                  toast.info('No images available in this gallery yet.');
                  return;
                }
              }

              if (isClickable) {
                openGallery(gallery);
              }
            }}
           className={`${isClickable ? 'cursor-pointer hover:opacity-90' : 'opacity-60 cursor-not-allowed'} bg-[#CEB46A] text-black px-3 sm:px-4 pt-3 pb-2 rounded-md shadow-md w-full max-w-full sm:max-w-md mx-auto relative transition-all overflow-hidden`}
>
             <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
               {/* Heart Icon to Favorite Gallery */}
               <button
                 onClick={(e) => {
                   e.stopPropagation();
                   toggleGalleryFavorite(gallery.name);
                 }}
                 className="transition-colors p-1 hover:scale-110 transform"
                 title={favoriteGalleries.has(gallery.name) ? "Remove from favorites" : "Add to favorites"}
               >
                 {favoriteGalleries.has(gallery.name) ? (
                   <FaHeart className="text-lg text-red-500" />
                 ) : (
                   <svg 
                     className="w-5 h-5 text-gray-700 hover:text-red-500 transition-colors" 
                     fill="none" 
                     stroke="currentColor" 
                     strokeWidth="2" 
                     viewBox="0 0 24 24"
                   >
                     <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                   </svg>
                 )}
               </button>
               <span className="text-black text-xs sm:text-sm font-semibold whitespace-nowrap">
                 {isTeaser ? "Free" : "TBD"}
               </span>
             </div>

            <div className="flex justify-center items-center gap-2 mb-2 flex-wrap">
              <h3 className={`text-base sm:text-lg font-bold text-center break-words ${!isTeaser && 'opacity-50'}`}>
                {gallery.name}
              </h3>
              <div className="flex items-center gap-1 text-xs sm:text-sm font-semibold flex-shrink-0">
                <FaCamera className="text-black" />
                <span>x{gallery.photos}</span>
              </div>
            </div>

            {/* Status Badge - Active when any image is in contest, Inactive otherwise */}
            {isTeaser ? (
              canUserAccessTeaser ? (
                gallery.hasActiveImage && gallery.photos > 0 ? (
                  <div className="absolute bottom-2 left-2 bg-green-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">
                    Active
                  </div>
                ) : (
                  <div className="absolute bottom-2 left-2 bg-gray-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">
                    Inactive
                  </div>
                )
              ) : (
                <div className="absolute bottom-2 left-2 bg-red-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">
                  Deposit Required
                </div>
              )
            ) : (
              // For locked categories - check if any image is in contest
              gallery.hasActiveImage && gallery.photos > 0 ? (
                <div className="absolute bottom-2 left-2 bg-green-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">
                  Active
                </div>
              ) : (
                <div className="absolute bottom-2 left-2 bg-gray-600 text-white px-2 sm:px-3 py-[2px] rounded-full text-[10px] sm:text-xs font-bold shadow-sm">
                  Inactive
                </div>
              )
            )}

            {isTeaser && !canUserAccessTeaser && (
              <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px] flex items-center justify-center text-center px-3 text-xs font-semibold text-white">
                Deposit funds to unlock this gallery.
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-1 sm:gap-2 mt-3">
             <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-2 sm:px-3 py-[2px] rounded-full font-semibold text-[10px] sm:text-xs">
                <FaTrophy className="text-[#ceb46a]" /> <span>x{gallery.trophies}</span>
              </div>
              <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-2 sm:px-3 py-[2px] rounded-full font-semibold text-[10px] sm:text-xs">
                <FaMedal className="text-[#ceb46a]" /> <span>x{gallery.medals || 0}</span>
              </div>
              <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-2 sm:px-3 py-[2px] rounded-full font-semibold text-[10px] sm:text-xs">
                <FaStar className="text-[#ceb46a]" /> <span>x{gallery.ribbons}</span>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Sub-modal - Gallery Images Grid */}
      {selectedGallery && (
        <div className="fixed inset-0 z-[999] bg-black bg-opacity-90 flex items-center justify-center px-4">
          <div className="bg-[#1C1C1E] text-white border border-[#ceb46a] rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setSelectedGallery(null)}
              className="absolute top-3 right-3 text-[#ceb46a] text-xl z-10"
              title="Close"
            >
              <FaTimes />
            </button>

            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-[#ceb46a]">{selectedGallery.name}</h2>
              <p className="text-sm mt-1 text-gray-300">
                {selectedGallery.price === "Free" ? "Free" : `${selectedGallery.price}`} •{" "}
                {selectedGallery.photos} {selectedGallery.photos === 1 ? 'photo' : 'photos'}
              </p>
            </div>

            <div className="flex justify-center gap-2 mb-6">
              <div className="flex items-center gap-1 bg-[#ceb46a] text-black px-3 py-1 rounded-full font-semibold text-xs shadow-sm">
                <FaTrophy /> x{selectedGallery.trophies}
              </div>
              <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-3 py-1 rounded-full font-semibold text-xs">
                <FaMedal className="text-[#ceb46a]" /> x{selectedGallery.medals || 0}
              </div>
              <div className="flex items-center gap-1 bg-[#1C1C1E] text-white border border-[#ceb46a] px-3 py-1 rounded-full font-semibold text-xs">
                <FaStar className="text-[#ceb46a]" /> x{selectedGallery.ribbons}
              </div>
            </div>

            {/* Images Grid */}
            {selectedGallery.images && selectedGallery.images.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {selectedGallery.images.map((image, idx) => {
                  console.log(`🖼️ Rendering image ${idx}:`, { id: image.id, url: image.image_url, isFavorite: favoriteImages.has(image.id) });
                  return (
                  <div 
                    key={image.id || idx} 
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image.image_url}
                      alt={`${selectedGallery.name} ${idx + 1}`}
                      className="w-full h-48 object-cover rounded-lg border-2 border-[#ceb46a] hover:border-[#d6c074] transition-all"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23333" width="200" height="200"/><text x="50%" y="50%" font-size="14" text-anchor="middle" dy=".3em" fill="%23ceb46a">Image Error</text></svg>';
                      }}
                    />
                    
                    {/* Favorite/Like Heart Icon */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('❤️ Heart clicked for image:', image.id);
                        toggleFavorite(image.id);
                      }}
                      className="absolute top-2 right-2 bg-black bg-opacity-60 p-2 rounded-full hover:bg-opacity-80 transition-all z-20"
                      title={favoriteImages.has(image.id) ? "Remove from favorites" : "Add to favorites"}
                    >
                      <FaHeart 
                        className={`text-xl ${favoriteImages.has(image.id) ? 'text-red-500' : 'text-gray-300'}`}
                      />
                    </button>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center pointer-events-none">
                      <span className="text-white opacity-0 group-hover:opacity-100 font-semibold text-sm">
                        View Full Size
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-400">No images available in this gallery</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Size Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex items-center justify-center px-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh]">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-[#ceb46a]"
              title="Close"
            >
              <FaTimes />
            </button>
            
            {/* Favorite button on full size image */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                console.log('❤️ Full-size heart clicked for image:', selectedImage.id);
                toggleFavorite(selectedImage.id);
              }}
              className="absolute top-4 right-4 bg-black bg-opacity-70 p-3 rounded-full hover:bg-opacity-90 transition-all z-20"
              title={favoriteImages.has(selectedImage.id) ? "Remove from favorites" : "Add to favorites"}
            >
              <FaHeart 
                className={`text-2xl ${favoriteImages.has(selectedImage.id) ? 'text-red-500' : 'text-white'}`}
              />
            </button>

            <img
              src={selectedImage.image_url}
              alt="Full size view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  </div>
)}



    </>
  );
};

export default CreatorCard;
